import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { FileText, Upload, Calendar, Award, AlertCircle, CheckCircle, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StudentPortal = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [submissionData, setSubmissionData] = useState({
    status: '',
    score: '',
    test_date: '',
    evidence: null
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('student_token');
    if (!token) {
      navigate('/student');
      return;
    }
    fetchUserData();
    fetchVouchers();
  }, [navigate]);

  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem('student_token');
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      toast.error('Session expired. Please login again.');
      localStorage.removeItem('student_token');
      navigate('/student');
    }
  };

  const fetchVouchers = async () => {
    try {
      const token = localStorage.getItem('student_token');
      const response = await axios.get(`${API}/student/vouchers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVouchers(response.data);
    } catch (error) {
      toast.error('Failed to fetch vouchers');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitScore = async (e) => {
    e.preventDefault();
    
    if (!submissionData.status) {
      toast.error('Please select Pass or Fail');
      return;
    }
    
    if (!submissionData.evidence) {
      toast.error('Please upload evidence PDF');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('student_token');
      const formData = new FormData();
      formData.append('evidence', submissionData.evidence);

      const params = new URLSearchParams({
        distribution_id: selectedVoucher.id,
        status: submissionData.status
      });
      
      if (submissionData.score) params.append('score', submissionData.score);
      if (submissionData.test_date) params.append('test_date', submissionData.test_date);

      await axios.post(`${API}/student/submit-score?${params.toString()}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Score submitted successfully!');
      setSelectedVoucher(null);
      setSubmissionData({ status: '', score: '', test_date: '', evidence: null });
      fetchVouchers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit score');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'Passed') {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
          <CheckCircle className="w-3 h-3" />
          Passed
        </span>
      );
    } else if (status === 'Failed') {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
          <XCircle className="w-3 h-3" />
          Failed
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
          <AlertCircle className="w-3 h-3" />
          Not Reported
        </span>
      );
    }
  };

  const isExpired = (expiryDate) => {
    return new Date(expiryDate) < new Date();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="student-portal">
      <div>
        <h2 className="text-4xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Welcome, {user?.name}!
        </h2>
        <p className="text-slate-600">View your certification vouchers and submit exam results</p>
      </div>

      {vouchers.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No vouchers assigned yet</h3>
            <p className="text-slate-500">Check back later for your certification vouchers</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {vouchers.map((voucher) => (
            <Card 
              key={voucher.id} 
              className={`border-2 ${
                isExpired(voucher.expiry_date) 
                  ? 'border-red-200 bg-red-50/30' 
                  : 'border-slate-200 hover:border-indigo-200'
              } transition-all`}
              data-testid={`voucher-card-${voucher.voucher_code}`}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Award className="w-5 h-5 text-indigo-600" />
                      {voucher.certification_type}
                    </CardTitle>
                    <CardDescription>Voucher Code: <span className="font-mono font-semibold">{voucher.voucher_code}</span></CardDescription>
                  </div>
                  {getStatusBadge(voucher.usage_status)}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Assigned Date</p>
                    <p className="font-medium">{new Date(voucher.assigned_date).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Due Date</p>
                    <p className="font-medium text-orange-600">{new Date(voucher.due_date).toLocaleDateString()}</p>
                  </div>
                 /* <div>
                    <p className="text-slate-500">Expiry Date</p>
                    <p className="font-medium text-red-600">{new Date(voucher.expiry_date).toLocaleDateString()}</p>
                  </div>*/
                  {voucher.score && (
                    <div>
                      <p className="text-slate-500">Score</p>
                      <p className="font-medium">{voucher.score}%</p>
                    </div>
                  )}
                </div>

                {isExpired(voucher.expiry_date) && (
                  <div className="bg-red-100 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                    <AlertCircle className="w-4 h-4 inline mr-2" />
                    This voucher has expired
                  </div>
                )}

                {voucher.usage_status === 'Not Reported' && !isExpired(voucher.expiry_date) && (
                  <Dialog open={selectedVoucher?.id === voucher.id} onOpenChange={(open) => !open && setSelectedVoucher(null)}>
                    <DialogTrigger asChild>
                      <Button 
                        onClick={() => setSelectedVoucher(voucher)}
                        className="w-full bg-gradient-to-r from-indigo-500 to-purple-400"
                        data-testid={`submit-score-button-${voucher.voucher_code}`}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Submit Exam Results
                      </Button>
                    </DialogTrigger>
                    <DialogContent data-testid="submit-score-dialog">
                      <DialogHeader>
                        <DialogTitle>Submit Exam Results</DialogTitle>
                        <DialogDescription>
                          Submit your results for {voucher.certification_type}
                        </DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleSubmitScore} className="space-y-4">
                        <div>
                          <Label htmlFor="status">Result *</Label>
                          <Select value={submissionData.status} onValueChange={(value) => setSubmissionData({...submissionData, status: value})}>
                            <SelectTrigger data-testid="status-select">
                              <SelectValue placeholder="Select Pass or Fail" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Passed">Passed</SelectItem>
                              <SelectItem value="Failed">Failed</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div>
                          <Label htmlFor="score">Score (Optional)</Label>
                          <Input
                            id="score"
                            type="number"
                            data-testid="score-input"
                            value={submissionData.score}
                            onChange={(e) => setSubmissionData({...submissionData, score: e.target.value})}
                            placeholder="85"
                            min="0"
                            max="100"
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="test_date">Test Date (Optional)</Label>
                          <Input
                            id="test_date"
                            type="date"
                            data-testid="test-date-input"
                            value={submissionData.test_date}
                            onChange={(e) => setSubmissionData({...submissionData, test_date: e.target.value})}
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="evidence">Evidence PDF *</Label>
                          <Input
                            id="evidence"
                            type="file"
                            accept=".pdf"
                            data-testid="evidence-input"
                            onChange={(e) => setSubmissionData({...submissionData, evidence: e.target.files[0]})}
                            required
                          />
                          <p className="text-sm text-slate-500 mt-1">Upload your exam certificate or score report</p>
                        </div>
                        
                        <Button 
                          type="submit" 
                          className="w-full bg-gradient-to-r from-indigo-500 to-purple-400"
                          disabled={submitting}
                          data-testid="submit-score-form-button"
                        >
                          {submitting ? 'Submitting...' : 'Submit Results'}
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default StudentPortal;
